import { IonButton, IonInput, IonSelect, IonSelectOption } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SelectModal } from '@/components/modals';
import { useAddDentist } from '@/contexts';
import { Service, getServiceOptions } from '@/data/Services';
import { useModal, useToast } from '@/hooks';
import {
  SERVICE_DURATION_OPTIONS,
  SERVICE_JOIN_SEPARATOR
} from '@/utils/constants';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { checkmarkCircleOutline } from 'ionicons/icons';

import './Services.css';

interface ServicesProps {
  presentingElement?: HTMLElement | undefined;
}

const Services: React.FC<ServicesProps> = ({ presentingElement }) => {
  const { services, setServices, onCreateServices, onSkip } = useAddDentist();
  const displayToast = useToast();

  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedServices, setLastSavedServices] = useState<Service[]>([]);
  const {
    isOpen: isSelectModalOpen,
    onOpen: onOpenSelectModal,
    onClose: onCloseSelectModal
  } = useModal();

  const {
    data: serviceOptions = [],
    isError
  }: UseQueryResult<string[], Error> = useQuery({
    queryKey: ['serviceOptions'],
    queryFn: getServiceOptions
  });

  useEffect(() => {
    if (isError) {
      displayToast({
        message: 'Please check your internet connection.',
        duration: 3000,
        position: 'bottom'
      });
    }
  }, [isError]);

  const copy = {
    subHeader: 'SERVICES',
    header: (
      <>
        Select the <Text color="primary">services</Text> you offer
      </>
    ),
    message: `Please check all services you offer and indicate the estimated duration of each service.`,
    buttonText: 'Done',
    skipText: 'Skip',
    inputLabel: 'Services',
    inputPlaceholder: 'Example: Braces Adjustment',
    modalTitle: (
      <>
        Select <Text color="primary">services</Text>
      </>
    )
  };

  const isSelected = (value: string) =>
    services.some((service) => service.name === value);

  const isPreSelected = (value: string) =>
    lastSavedServices.some((service) => service.name === value);

  const onSelect = (value: string, selected: boolean) => {
    setServices((prevServices) =>
      selected
        ? prevServices.filter((service) => service.name !== value)
        : [...prevServices, { name: value, duration: 30 }]
    );
  };

  const onCloseWithSave = () => {
    onCloseSelectModal();
    setLastSavedServices(services);
  };

  const onDurationChange = (service: Service, value: number) => {
    setServices((prevServices) =>
      prevServices.map((s) =>
        s.name === service.name ? { ...s, duration: value } : s
      )
    );
  };

  const onNext = useCallback(() => {
    if (services.length > 0) {
      setHasError(false);
      onCreateServices(
        services.map((service) =>
          service.duration ? service : { ...service, duration: 30 }
        )
      );
    } else {
      setHasError(true);
      displayToast({
        message: 'Please select a valid service.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  }, [services, onCreateServices]);

  return (
    <div className="services">
      <Spacer top={32} bottom={24}>
        <h4 className="services__sub-header">{copy.subHeader}</h4>
        <h1 className="services__header">{copy.header}</h1>
        <p className="services__message">{copy.message}</p>
      </Spacer>
      <div className="services__form">
        <div
          className={`services__input ${hasError ? 'services__input--error' : ''}`}
        >
          <IonInput
            id="service-input"
            label={copy.inputLabel}
            labelPlacement="stacked"
            value={services
              .map((service) => service.name)
              .join(SERVICE_JOIN_SEPARATOR)}
            placeholder={copy.inputPlaceholder}
            onClick={() => {
              setHasError(false);
              onOpenSelectModal();
            }}
            readonly
          />
        </div>
      </div>
      {services.length > 0 && (
        <Spacer top={24}>
          <div className="services__selected-services">
            {services
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((service, index) => (
                <div key={index} className="services__selected-service">
                  <p className="services__service-label">{service.name}</p>
                  <div className="services__input">
                    <IonSelect
                      label="Estimated Duration"
                      labelPlacement="stacked"
                      placeholder="Select duration"
                      value={service.duration}
                      onIonChange={(e: CustomEvent) =>
                        onDurationChange(service, e.detail.value)
                      }
                    >
                      {SERVICE_DURATION_OPTIONS.map((option) => (
                        <IonSelectOption
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                </div>
              ))}
          </div>
        </Spacer>
      )}
      <Spacer bottom={172} />
      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={checkmarkCircleOutline} />
          {copy.buttonText}
        </IonButton>
        <IonButton
          id="skip-button"
          onClick={onSkip}
          expand="block"
          fill="clear"
          size="default"
          color="primary"
          strong={true}
        >
          {copy.skipText}
        </IonButton>
      </ButtonContainer>

      <SelectModal
        isOpen={isSelectModalOpen}
        presentingElement={presentingElement}
        title={copy.modalTitle}
        options={serviceOptions}
        isSelected={isSelected}
        isPreSelected={isPreSelected}
        onSelect={onSelect}
        onClose={onCloseWithSave}
        multiple={true}
        showOthers={false}
      />
    </div>
  );
};

export default Services;
